import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useToolbarContextOrNull } from '../../../context/ToolbarContext';
import { useMetaspaceEvents } from '../../../hooks/useMetaspaceEvents';
import { getGlyphIconDataUri } from '../../../utils/avatarIcons';
import type { TaskDialProps } from './types';
import './TaskDial.scss';

/**
 * Task status colors for donut segments
 */
const TASK_STATUS_COLORS: Record<string, string> = {
  pending: '#6c757d',
  in_progress: '#0d6efd',
  complete: '#198754',
  overdue: '#dc3545',
};

/**
 * Display order for task status segments
 */
const TASK_STATUS_ORDER = ['pending', 'in_progress', 'complete', 'overdue'];

/**
 * Calculate SVG arc path for a donut segment
 */
function describeArc(
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number
): string {
  const startRad = ((startAngle - 90) * Math.PI) / 180;
  const endRad = ((endAngle - 90) * Math.PI) / 180;

  const outerStartX = cx + outerRadius * Math.cos(startRad);
  const outerStartY = cy + outerRadius * Math.sin(startRad);
  const outerEndX = cx + outerRadius * Math.cos(endRad);
  const outerEndY = cy + outerRadius * Math.sin(endRad);

  const innerStartX = cx + innerRadius * Math.cos(startRad);
  const innerStartY = cy + innerRadius * Math.sin(startRad);
  const innerEndX = cx + innerRadius * Math.cos(endRad);
  const innerEndY = cy + innerRadius * Math.sin(endRad);

  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStartX} ${outerStartY}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEndX} ${outerEndY}`,
    `L ${innerEndX} ${innerEndY}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStartX} ${innerStartY}`,
    'Z',
  ].join(' ');
}

interface StatusGroup {
  status: string;
  count: number;
}

interface Segment extends StatusGroup {
  percentage: number;
  startAngle: number;
  endAngle: number;
  color: string;
}

/**
 * TaskDial component - displays task status breakdown as a donut chart
 * using ArmillaryContext task data. Clicking a segment emits a
 * TASK_FILTER_TRIGGERED event for toolbar filtering.
 */
const TaskDial: React.FC<TaskDialProps> = ({
  sphere,
  width,
  height,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const toolbarContext = useToolbarContextOrNull();
  const { emitTaskFilter } = useMetaspaceEvents({ subscriberId: 'task-dial' });
  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');

  const tasks = toolbarContext?.taskState?.tasks ?? [];

  // Group tasks by status
  const statusGroups = useMemo<StatusGroup[]>(() => {
    if (!tasks.length) return [];

    const counts: Record<string, number> = {};
    for (const task of tasks) {
      const status = task.status || 'pending';
      counts[status] = (counts[status] || 0) + 1;
    }

    return TASK_STATUS_ORDER
      .filter((status) => counts[status] > 0)
      .map((status) => ({ status, count: counts[status] }));
  }, [tasks]);

  const total = useMemo(() => statusGroups.reduce((sum, g) => sum + g.count, 0), [statusGroups]);

  // Calculate arc segments
  const segments = useMemo<Segment[]>(() => {
    if (!statusGroups.length || total === 0) return [];

    let currentAngle = 0;
    return statusGroups.map((group) => {
      const percentage = (group.count / total) * 100;
      const sweepAngle = (group.count / total) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + sweepAngle;
      currentAngle = endAngle;

      return {
        ...group,
        percentage,
        startAngle,
        endAngle,
        color: TASK_STATUS_COLORS[group.status] || TASK_STATUS_COLORS.pending,
      };
    });
  }, [statusGroups, total]);

  // Fetch sphere image for center avatar
  useEffect(() => {
    setImageUrl('');
  }, [sphere?.id]);

  useEffect(() => {
    let isMounted = true;

    const fetchImage = async () => {
      if (!apiClients?.graphClient || !sphere?.id || !sphere?.properties?.imageId) {
        return;
      }

      try {
        const cachedData = await globalSphereCache.getSphereData(
          sphere.id,
          apiClients.graphClient
        );

        if (isMounted) {
          setImageUrl(cachedData.imageUrl || cachedData.thumbnailUrl || '');
        }
      } catch {
        // Image fetch failed silently
      }
    };

    const imageId = sphere?.properties?.imageId;
    if (
      sphere?.id &&
      imageId &&
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(imageId)
    ) {
      fetchImage();
    }

    return () => {
      isMounted = false;
    };
  }, [sphere?.id, sphere?.properties?.imageId, apiClients?.graphClient]);

  // Handle segment click with toggle behavior
  const handleSegmentClick = useCallback(
    (status: string) => {
      const newStatus = activeStatus === status ? null : status;
      setActiveStatus(newStatus);
      emitTaskFilter(newStatus, sphere?.id);
    },
    [activeStatus, emitTaskFilter, sphere?.id]
  );

  // SVG dimensions
  const size = Math.min(width, height);
  const center = size / 2;
  const outerRadius = size * 0.48;
  const innerRadius = size * 0.32;
  const avatarRadius = size * 0.28;

  // Get fallback icon
  const iconDataUri = getGlyphIconDataUri(sphere?.glyph, sphere?.glyphColor);

  /**
   * Get the localized label for a task status
   */
  const getStatusLabel = (status: string): string => {
    const keyMap: Record<string, string> = {
      pending: 'arc.stage.task.pending',
      in_progress: 'arc.stage.task.in_progress',
      complete: 'arc.stage.task.complete',
      overdue: 'arc.stage.task.overdue',
    };
    return t(keyMap[status] || status);
  };

  if (!sphere) {
    return (
      <div className="task-dial task-dial--empty">
        <div className="task-dial__placeholder" />
      </div>
    );
  }

  // No tasks: show empty state with gray ring
  if (total === 0) {
    return (
      <div className="task-dial task-dial--no-tasks">
        <svg
          className="task-dial__chart"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden="true"
        >
          <circle
            cx={center}
            cy={center}
            r={(outerRadius + innerRadius) / 2}
            fill="none"
            stroke="rgba(255,255,255,0.15)"
            strokeWidth={outerRadius - innerRadius}
          />
          <circle
            cx={center}
            cy={center}
            r={avatarRadius}
            className="task-dial__avatar-bg"
          />
        </svg>
        <div
          className="task-dial__avatar"
          style={{ width: avatarRadius * 2, height: avatarRadius * 2 }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={sphere.properties?.name || t('common.sphere')}
              className="task-dial__avatar-img"
            />
          ) : (
            <img
              src={iconDataUri}
              alt={sphere.properties?.name || t('common.sphere')}
              className="task-dial__avatar-icon"
            />
          )}
        </div>
        <div className="task-dial__stats">
          <span className="task-dial__label">{t('taskDial.noTasks')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="task-dial">
      {/* Donut Chart SVG */}
      <svg
        className="task-dial__chart"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        {/* Background ring */}
        <circle
          cx={center}
          cy={center}
          r={(outerRadius + innerRadius) / 2}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={outerRadius - innerRadius}
        />

        {/* Donut segments */}
        {segments.map((segment, index) => {
          if (segment.endAngle - segment.startAngle < 1) return null;

          const isActive = activeStatus === segment.status;
          const isSingleSegment = segments.length === 1;
          const gap = isSingleSegment ? 0 : 0.5;

          return (
            <path
              key={segment.status || index}
              d={
                isSingleSegment
                  ? describeArc(center, center, outerRadius, innerRadius, 0, 359.99)
                  : describeArc(
                      center,
                      center,
                      outerRadius,
                      innerRadius,
                      segment.startAngle,
                      segment.endAngle - gap
                    )
              }
              fill={segment.color}
              className={`task-dial__segment${isActive ? ' task-dial__segment--active' : ''}`}
              onClick={() => handleSegmentClick(segment.status)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSegmentClick(segment.status);
                }
              }}
              aria-label={`${getStatusLabel(segment.status)}: ${segment.count} (${segment.percentage.toFixed(0)}%)`}
            >
              <title>{`${getStatusLabel(segment.status)}: ${segment.count} (${segment.percentage.toFixed(0)}%)`}</title>
            </path>
          );
        })}

        {/* Center avatar circle background */}
        <circle
          cx={center}
          cy={center}
          r={avatarRadius}
          className="task-dial__avatar-bg"
        />
      </svg>

      {/* Center avatar */}
      <div
        className="task-dial__avatar"
        style={{
          width: avatarRadius * 2,
          height: avatarRadius * 2,
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={sphere.properties?.name || t('common.sphere')}
            className="task-dial__avatar-img"
          />
        ) : (
          <img
            src={iconDataUri}
            alt={sphere.properties?.name || t('common.sphere')}
            className="task-dial__avatar-icon"
          />
        )}
      </div>

      {/* Bottom stats */}
      <div className="task-dial__stats">
        <span className="task-dial__total">{total}</span>
        {activeStatus ? (
          <span className="task-dial__label task-dial__label--filtered">
            {getStatusLabel(activeStatus)}
          </span>
        ) : (
          <span className="task-dial__label">
            {t('arc.stage.task.todo')}
          </span>
        )}
      </div>
    </div>
  );
};

export default TaskDial;
